/**
 * AIDiscoveryResults — Inline dropdown preview for AI Discovery results.
 * 
 * Shows a compact preview of products, brands, and suggestion chips
 * in a dropdown below the AIDiscoveryBar. Used in NavBar and Hero variants.
 */

import React, { useEffect, useRef } from 'react';
import { getProductDetailUrl, getBrandDetailUrl } from '../utils/aiDiscoverRouting';
import './AIDiscoveryResults.css';

const AIDiscoveryResults = ({
  results,
  isLoading,
  error,
  isRateLimited,
  query,
  market,
  onClose,
  onSuggestionClick,
  onViewAll,
}) => {
  const dropdownRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Loading state
  if (isLoading) {
    return (
      <div className="ai-results-dropdown" ref={dropdownRef} role="listbox" aria-busy="true">
        <div className="ai-results-loading">
          <div className="ai-results-typing-indicator">
            <span className="ai-typing-dot" />
            <span className="ai-typing-dot" />
            <span className="ai-typing-dot" />
          </div>
          <span className="ai-results-loading-text">Finding products for you...</span>
        </div>
      </div>
    );
  }

  // Rate limited
  if (isRateLimited) {
    return (
      <div className="ai-results-dropdown" ref={dropdownRef}>
        <div className="ai-results-rate-limited">
          <span className="ai-results-rate-icon">⏳</span>
          <p>Too many searches. Please wait a moment and try again.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="ai-results-dropdown" ref={dropdownRef}>
        <div className="ai-results-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No results
  if (!results) return null;

  const { products = [], brands = [], suggestions = [] } = results;
  const totalResults = products.length + brands.length;

  if (totalResults === 0 && query) {
    return (
      <div className="ai-results-dropdown" ref={dropdownRef}>
        <div className="ai-results-empty">
          <p>No products found for "{query}"</p>
          <p className="ai-results-empty-hint">Try different terms or browse our collections</p>
        </div>
      </div>
    );
  }

  if (totalResults === 0) return null;

  const displayProducts = products.slice(0, 6);
  const displayBrands = brands.slice(0, 3);

  return (
    <div className="ai-results-dropdown" ref={dropdownRef} role="listbox" aria-label="AI Discovery results">
      {/* Products Section */}
      {displayProducts.length > 0 && (
        <div className="ai-results-section">
          <h4 className="ai-results-section-title">Products</h4>
          <div className="ai-results-products-list">
            {displayProducts.map((product) => {
              const isCanadian = (product.country || '').toLowerCase() === 'canada';
              return (
                <a
                  key={product._id || product.productId}
                  href={getProductDetailUrl(product)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ai-results-product-mini"
                  role="option"
                >
                  {product.image_url || product.imageUrl || product.imagePath ? (
                    <img
                      src={product.image_url || product.imageUrl || product.imagePath}
                      alt={product.name || product.title}
                      className="ai-results-product-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="ai-results-product-img ai-results-product-img--placeholder">
                      🛍️
                    </div>
                  )}
                  <div className="ai-results-product-info">
                    <span className="ai-results-product-name">
                      {product.name || product.title}
                    </span>
                    <span className="ai-results-product-meta">
                      <span className="ai-results-country-badge">
                        {isCanadian ? '🇨🇦' : '🇺🇸'}
                      </span>
                      {product.price && product.price > 0 && (
                        <span className="ai-results-product-price">
                          {isCanadian ? 'CA' : ''}${product.price.toFixed(2)}
                        </span>
                      )}
                      <span className="ai-results-new-tab-icon" aria-label="Opens in new tab">↗</span>
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Brands Section */}
      {displayBrands.length > 0 && (
        <div className="ai-results-section">
          <h4 className="ai-results-section-title">Brands</h4>
          <div className="ai-results-brands-list">
            {displayBrands.map((brand) => (
              <a
                key={brand._id || brand.brandId}
                href={getBrandDetailUrl(brand)}
                target="_blank"
                rel="noopener noreferrer"
                className="ai-results-brand-mini"
                role="option"
              >
                <span className="ai-results-brand-name">{brand.name || brand.brandName}</span>
                <span className="ai-results-new-tab-icon" aria-label="Opens in new tab">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="ai-results-section ai-results-suggestions-section">
          <div className="ai-results-suggestions">
            {suggestions.slice(0, 3).map((suggestion, i) => (
              <button
                key={i}
                className="ai-results-suggestion-chip"
                onClick={() => onSuggestionClick?.(suggestion)}
                role="option"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* View All */}
      {totalResults > 0 && (
        <div className="ai-results-footer">
          <button className="ai-results-view-all" onClick={() => onViewAll?.(query)}>
            View all {products.length} results →
          </button>
        </div>
      )}
    </div>
  );
};

export default AIDiscoveryResults;
